// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { NavigationLink } from '@/components/navigation-link';
import { Box, Chip, Paper, Stack, Typography, useTheme } from '@mui/material';
import { Link } from '@tanstack/react-router';
import { ReactNode, forwardRef, useCallback, useState } from 'react';

interface NavigationDropdownItem {
  label: string;
  description: string;
  to: string;
  icon: ReactNode;
  disabled?: boolean;
  dropdownChip?: string;
}

interface NavigationDropdownProps {
  label: string;
  icon: ReactNode;
  items: NavigationDropdownItem[];
  isActive: boolean;
  activeSubsection?: string | null;
  onSubsectionClick?: (subsectionLabel: string) => void;
}

/**
 * CSS-based navigation dropdown with minimal JavaScript for menu item clicks.
 * Uses CSS :hover selectors for show/hide with temporary state override on click.
 */
export const NavigationDropdown = forwardRef<
  HTMLDivElement,
  NavigationDropdownProps
>(function NavigationDropdown(props, ref) {
  const theme = useTheme();
  const [forceHide, setForceHide] = useState(false);

  // Hide dropdown when menu item is clicked
  const { onSubsectionClick } = props;
  const handleMenuItemClick = useCallback(
    (subsectionLabel: string) => {
      // Call the subsection click handler to update active section and subsection
      onSubsectionClick?.(subsectionLabel);

      setForceHide(true);
      // Reset after a short delay to allow CSS hover to work again
      setTimeout(() => {
        setForceHide(false);
      }, 100);
    },
    [onSubsectionClick]
  );

  return (
    <Box
      ref={ref}
      sx={{
        position: 'relative',
        // Show dropdown on hover (unless force hidden)
        '&:hover .dropdown-menu': {
          display: forceHide ? 'none' : 'block',
        },
        // Hide other dropdowns when this one is hovered
        '&:hover ~ * .dropdown-menu': {
          display: 'none !important',
        },
      }}
    >
      <NavigationLink
        icon={props.icon}
        label={props.label}
        isActive={props.isActive}
      />

      <Paper
        className="dropdown-menu"
        elevation={8}
        sx={{
          display: 'none',
          position: 'absolute',
          top: '100%',
          left: 0,
          minWidth: '28rem',
          marginTop: 0,
          zIndex: theme.zIndex.modal,
          py: 1,
          // Responsive positioning to prevent overflow on right side of screen
          [theme.breakpoints.down('lg')]: {
            right: 0,
            left: 'auto',
            minWidth: '20rem',
          },
          [theme.breakpoints.down('sm')]: {
            minWidth: '16rem',
          },
        }}
      >
        {props.items.map((item) => {
          // Highlight subsection if it matches the active subsection
          const isActive =
            props.isActive && props.activeSubsection === item.label;
          const isDisabled = item.disabled || false;

          return (
            <Box
              key={item.to}
              component={isDisabled ? 'div' : Link}
              to={isDisabled ? undefined : item.to}
              onClick={
                isDisabled ? undefined : () => handleMenuItemClick(item.label)
              }
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                paddingX: 2,
                paddingY: 1.5,
                borderRadius: 1,
                marginX: 1,
                marginY: 0.5,
                textDecoration: 'none',
                cursor: isDisabled ? 'default' : 'pointer',
                color: isDisabled
                  ? theme.palette.text.disabled
                  : isActive
                    ? theme.palette.primary.main
                    : theme.palette.text.secondary,
                backgroundColor:
                  isActive && !isDisabled
                    ? theme.palette.primary.light + '20'
                    : 'transparent',
                '&:hover': isDisabled
                  ? {}
                  : {
                      backgroundColor: theme.palette.primary.light + '10',
                    },
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  color: isDisabled
                    ? theme.palette.text.disabled
                    : isActive
                      ? theme.palette.primary.main
                      : 'inherit',
                }}
              >
                {item.icon}
              </Box>
              <Stack sx={{ flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    variant="body2"
                    fontWeight={isActive && !isDisabled ? 600 : 400}
                    color={
                      isDisabled
                        ? 'text.disabled'
                        : isActive
                          ? theme.palette.primary.main
                          : 'text.primary'
                    }
                  >
                    {item.label}
                  </Typography>
                  {item.dropdownChip && (
                    <Chip
                      label={item.dropdownChip}
                      size="small"
                      variant="outlined"
                      sx={{
                        height: '18px',
                        fontSize: '0.625rem',
                        fontWeight: 500,
                        color: theme.palette.text.secondary,
                        borderColor: theme.palette.divider,
                        backgroundColor: 'transparent',
                      }}
                    />
                  )}
                </Box>
                <Typography
                  variant="caption"
                  color={isDisabled ? 'text.disabled' : 'text.secondary'}
                >
                  {item.description}
                </Typography>
              </Stack>
            </Box>
          );
        })}
      </Paper>
    </Box>
  );
});
